import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogActions,
  Typography,
  Avatar,
  Divider,
  IconButton,
  LinearProgress,
  Chip,
} from "@mui/material";
import React, { useRef, useState } from "react";
import api from "../../axiosInstance";
import { toast } from "react-toastify";
import CloseIcon from "@mui/icons-material/Close";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";

const ACCEPTED_EXTENSIONS = [".csv", ".xls", ".xlsx"];

const formatFileSize = (bytes) => {
  if (!bytes && bytes !== 0) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

function UploadExamTimeTableModal({ open, onClose, onSuccess }) {
  const [file, setFile] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [fileError, setFileError] = useState("");
  const fileInputRef = useRef(null);

  const isValidFile = (candidate) => {
    const name = candidate?.name?.toLowerCase() || "";
    return ACCEPTED_EXTENSIONS.some((ext) => name.endsWith(ext));
  };

  const handleFileSelect = (candidate) => {
    if (!candidate) return;
    if (!isValidFile(candidate)) {
      setFileError("Only CSV or Excel (.csv, .xls, .xlsx) files are supported.");
      return;
    }
    setFileError("");
    setFile(candidate);
  };

  const handleInputChange = (e) => {
    handleFileSelect(e.target.files?.[0]);
    e.target.value = "";
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    handleFileSelect(e.dataTransfer?.files?.[0]);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => setIsDragging(false);

  const handleRemoveFile = () => {
    setFile(null);
    setFileError("");
    setProgress(0);
  };

  const resetState = () => {
    setFile(null);
    setFileError("");
    setUploading(false);
    setProgress(0);
    setIsDragging(false);
  };

  const handleClose = () => {
    if (uploading) return;
    resetState();
    onClose();
  };

  const handleUpload = async () => {
    if (!file) {
      setFileError("Please select a file to upload.");
      return;
    }

    setUploading(true);
    setProgress(0);

    try {
      const formData = new FormData();
      formData.append("file", file);

      await api.post("/api/admin/exams/upload", formData, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (event) => {
          if (!event.total) return;
          setProgress(Math.round((event.loaded * 100) / event.total));
        },
      });

      toast.success("Exam time table uploaded successfully!", {
        position: "top-right",
        autoClose: 2000,
      });

      resetState();
      onSuccess?.();
      onClose();
    } catch (error) {
      const errorMessage =
        error.response?.data?.errorMessage ||
        "Failed to upload exam time table. Please try again.";
      toast.error(errorMessage, { autoClose: 3000 });
      setUploading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{ sx: { borderRadius: 3, overflow: "hidden" } }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          px: 3,
          py: 2.5,
          background: "linear-gradient(135deg, #17479e 0%, #2f6fdb 100%)",
          color: "#fff",
        }}
      >
        <Avatar sx={{ bgcolor: "rgba(255,255,255,0.18)", color: "#fff" }}>
          <UploadFileIcon />
        </Avatar>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
            Upload Exam Time Table
          </Typography>
          <Typography variant="caption" sx={{ opacity: 0.85 }}>
            Import exams in bulk from a CSV or Excel file
          </Typography>
        </Box>
        <IconButton
          onClick={handleClose}
          size="small"
          disabled={uploading}
          sx={{ color: "#fff" }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      <DialogContent sx={{ pt: 3 }}>
        <Box
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onClick={() => !uploading && fileInputRef.current?.click()}
          sx={{
            border: "2px dashed",
            borderColor: isDragging ? "primary.main" : "divider",
            borderRadius: 3,
            py: 5,
            px: 3,
            textAlign: "center",
            cursor: uploading ? "default" : "pointer",
            backgroundColor: isDragging ? "action.hover" : "transparent",
            transition: "all 0.2s ease-in-out",
          }}
        >
          <input
            ref={fileInputRef}
            type="file"
            hidden
            accept={ACCEPTED_EXTENSIONS.join(",")}
            onChange={handleInputChange}
          />
          <CloudUploadIcon
            sx={{ fontSize: 44, color: "primary.main", mb: 1 }}
          />
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            Drag & drop your file here, or click to browse
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Supports CSV, XLS, XLSX
          </Typography>
        </Box>

        {fileError && (
          <Typography
            variant="caption"
            color="error"
            sx={{ display: "block", mt: 1 }}
          >
            {fileError}
          </Typography>
        )}

        {file && (
          <Box
            sx={{
              mt: 2.5,
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              p: 1.5,
              borderRadius: 2,
              border: "1px solid",
              borderColor: "divider",
            }}
          >
            <InsertDriveFileIcon color="primary" />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="body2" noWrap sx={{ fontWeight: 600 }}>
                {file.name}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {formatFileSize(file.size)}
              </Typography>
              {uploading && (
                <LinearProgress
                  variant="determinate"
                  value={progress}
                  sx={{ mt: 1, borderRadius: 1, height: 6 }}
                />
              )}
            </Box>
            {!uploading && (
              <IconButton size="small" onClick={handleRemoveFile}>
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            )}
            {uploading && (
              <Chip label={`${progress}%`} size="small" color="primary" />
            )}
          </Box>
        )}
      </DialogContent>

      <Divider />

      <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
        <Button
          onClick={handleClose}
          variant="outlined"
          disabled={uploading}
          sx={{ borderRadius: 2, textTransform: "none", fontWeight: 600 }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleUpload}
          variant="contained"
          disabled={uploading || !file}
          sx={{
            borderRadius: 2,
            textTransform: "none",
            fontWeight: 600,
            minWidth: 120,
            background: "linear-gradient(135deg, #17479e 0%, #2f6fdb 100%)",
            "&:hover": {
              background: "linear-gradient(135deg, #123a80 0%, #255bb5 100%)",
            },
          }}
        >
          {uploading ? `Uploading... ${progress}%` : "Upload"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default UploadExamTimeTableModal;
